<script setup lang="ts">
  import {
    computed,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
    PropType,
  } from 'vue';
  import ChevronDownIcon from '@primevue/icons/chevrondown';
  // @ts-expect-error - Missing types for SlVueTreeNext
  import { SlVueTreeNext } from 'sl-vue-tree-next';
  import {
    faChevronRight,
    faChevronDown,
    faX,
    faRotate,
    faRotateRight,
    faFilter,
    faSpinnerThird,
  } from '@fortawesome/pro-regular-svg-icons';
  import { faFilter as faFilterSolid } from '@fortawesome/pro-solid-svg-icons';
  import Checkbox from 'primevue/checkbox';
  import {
    NodeModel,
    TreeNode,
    SlVueTreeSelectData,
    LoadFunction,
  } from './KksSelector.types';
  import Tag from 'primevue/tag';
  import { KKSStatesColorTags } from './kksStatesColorTags.ts';
  import { debounce, find } from 'lodash';
  import IconField from 'primevue/iconfield';
  import InputIcon from 'primevue/inputicon';
  import InputText from 'primevue/inputtext';
  import { useI18n } from 'vue-i18n';
  import InputGroup from 'primevue/inputgroup';
  import Button from 'primevue/button';
  import Popover from 'primevue/popover';
  import Select from 'primevue/select';
  import MultiSelect from 'primevue/multiselect';
  import { ITreeSelectKKS, KksState, SlVueTreeSearchOptions } from 'types';
  import { KksTreeService } from '@/services';
  import { useWorkspaceKks } from '@/composables/api/index.ts';

  /** PROPS & EMITS & MODEL */
  const props = defineProps({
    label: {
      type: String,
      required: true,
    },
    value: {
      type: Array as PropType<NodeModel<any>[]>,
      required: true,
    },
    load: {
      type: Function as PropType<LoadFunction>,
      required: true,
    },
    tagKey: {
      type: String,
      required: true,
    },
    filterOption: {
      type: Array as PropType<string[]>,
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    location: {
      type: String,
      required: true,
    },
    canRemoveLast: {
      type: Boolean,
      default: true,
    },
    showFilterButton: {
      type: Boolean,
      default: true,
    },
    workspaceId: {
      type: String,
      required: false,
    },
  });
  const model = defineModel<SlVueTreeSelectData[]>({ default: [] });

  /* DATA */
  const expanded = ref(false);
  const nodes = ref<NodeModel<SlVueTreeSelectData>[]>();
  const selectedValue = ref<any[]>([]);
  const slVueTree = ref();
  const checkBoxes = ref<Record<string, boolean>>({});
  const spinners = ref<Record<string, boolean>>({});
  const dropdown = ref<HTMLElement | null>(null);
  const searchValue = ref<string>('');
  const showMore = ref<Record<string, any>>({});
  const searchFilter = ref();
  const searchOption = ref<string[]>([]);
  const selectedSearchOption = ref<string>(SlVueTreeSearchOptions.STARTS_WITH);
  const searchLoad = ref<boolean>(false);
  const selectedFilters = ref<string[] | null>([]);
  const activeFilter = ref<boolean>(false);
  const loadingSpinner = ref<boolean>(false);
  const freeTextInputValue = ref<string>('');
  const isUnathorizedForKksError = ref<boolean>(false);
  const isDropdownLoading = computed(() => {
    return props.busy || loadingSpinner.value || searchLoad.value;
  });

  /* COMPOSABLES */
  const { t } = useI18n();
  const { validateKksAccess, isLoadingKksAccess } = useWorkspaceKks();

  /** WATCH */
  watch(expanded, () => {
    syncNodes();

    if (!expanded.value) {
      resetFilter();
      searchValue.value = '';
    }
  });

  watch(
    () => props.value,
    () => {
      syncNodes();
    },
    { deep: true },
  );

  watch(
    selectedValue,
    (newSelected) => {
      Object.keys(checkBoxes.value).forEach((id) => {
        const exists = find(newSelected, (sc) => sc._id === id);
        if (!exists) {
          checkBoxes.value[id] = false;
        }
      });
    },
    { deep: true },
  );

  watch(model, setPreselected, { deep: true });

  /** METHODS */
  const getUniqueSelectedValues = (
    values: SlVueTreeSelectData[],
  ): SlVueTreeSelectData[] => {
    const selectedById = new Map<string, SlVueTreeSelectData>();

    values.forEach((value) => {
      if (value._id) {
        selectedById.set(value._id, value);
      }
    });

    return Array.from(selectedById.values());
  };

  const syncNodes = () => {
    if (
      expanded.value &&
      (!nodes.value || nodes.value?.length < 1 || nodes.value !== props.value)
    ) {
      nodes.value = props.value;
    }
  };

  const updateValue = (newValue: SlVueTreeSelectData[]) => {
    const uniqueSelectedValues = getUniqueSelectedValues(newValue);

    selectedValue.value = [...uniqueSelectedValues];
    model.value = [...uniqueSelectedValues];
  };

  const loadChildren = async (node: TreeNode<SlVueTreeSelectData>) => {
    if (!node.isExpanded && (!node.children || node.children.length < 1)) {
      spinners.value[node.data?._id as string] = true;

      const children = await props.load(
        node.data?._id,
        '',
        0,
        50,
        '',
        selectedFilters.value || null,
      );

      if (children.data.length > 0) {
        if (children.data.length < children.count) {
          const lastChild = children.data[children.data.length - 1];
          if (lastChild.data) {
            showMore.value[lastChild.data?._id] = {
              expandedCount: 1,
              offset: 0,
              limit: 100,
              parentId: node.data?._id,
              parentPath: node.path,
              loading: false,
            };
          }
        }

        slVueTree.value.updateNode({
          path: node.path,
          patch: {
            children: [...children.data],
          },
        });
        spinners.value[node.data?._id as string] = false;
      } else {
        spinners.value[node.data?._id as string] = false;
        delete spinners.value[node.data?._id as string];
        slVueTree.value.updateNode({
          path: node.path,
          patch: {
            isLeaf: true,
          },
        });
      }
    }
  };

  const chooseElement = (node: TreeNode<SlVueTreeSelectData>) => {
    if (node.data?._id) {
      if (checkBoxes.value[node.data?._id]) {
        updateValue([...selectedValue.value, node.data as SlVueTreeSelectData]);
      } else {
        updateValue(
          selectedValue.value.filter(
            (selectedNode) => selectedNode._id !== node.data?._id,
          ),
        );
      }
    }
  };

  const addFromFreeTextInput = async () => {
    const value = freeTextInputValue.value.trim();
    if (!value) return;
    if (props.workspaceId) {
      const isAuthorized = await validateKksAccess(props.workspaceId, [value]);
      if (!isAuthorized) {
        isUnathorizedForKksError.value = true;
        return;
      }
    }

    const [node] = await KksTreeService.getPreselectedKksNodes(
      [value],
      props.location,
    );
    const alreadySelected = isKksPresent(node);
    if (!alreadySelected) {
      updateValue([...selectedValue.value, node]);
    }
    freeTextInputValue.value = '';
  };

  const removeElement = (kks?: SlVueTreeSelectData) => {
    if (kks) {
      updateValue(
        selectedValue.value.filter(
          (selectedNode) => selectedNode._id !== kks._id,
        ),
      );
    } else {
      updateValue([]);
    }
  };

  const onClickOutside = (event: any) => {
    const target = event.target;
    const dropdownContainer = dropdown.value;

    const multiSelectDropdown = document.querySelector(
      '.p-multiselect-overlay',
    );
    if (
      dropdownContainer &&
      !dropdownContainer.contains(target) &&
      !(multiSelectDropdown && multiSelectDropdown.contains(target))
    ) {
      expanded.value = false;
    }
  };

  const onSearch = async () => {
    searchLoad.value = true;
    const searchResult = await props.load(
      null,
      searchValue.value,
      0,
      200,
      selectedSearchOption.value,
      selectedFilters.value || null,
    );
    nodes.value = searchResult.data;
    if (nodes.value.length < searchResult.count) {
      const lastNode = nodes.value[nodes.value.length - 1];
      if (lastNode.data) {
        showMore.value[lastNode.data._id] = {
          expandedCount: 1,
          offset: 0,
          limit: 200,
          loading: false,
        };
      }
    }
    searchLoad.value = false;
  };

  const delay = debounce((cb: Function) => {
    cb();
  }, 600);

  const loadMore = async (node: NodeModel<any>) => {
    if (showMore.value[node.data?._id as string]) {
      showMore.value[node.data?._id as string].loading = true;
      const parentId = showMore.value[node.data?._id as string].parentId;
      const expandedCount =
        showMore.value[node.data?._id as string].expandedCount;
      const limit = showMore.value[node.data?._id as string].limit;
      const offset = expandedCount * limit;
      const parentPath = showMore.value[node.data?._id as string].parentPath;
      let parent;

      const children = await props.load(
        parentId,
        searchValue.value || '',
        offset,
        limit,
        searchValue.value ? selectedSearchOption.value : '',
        selectedFilters.value || null,
      );

      if (!searchValue.value) {
        parent = slVueTree.value.getNode(parentPath);
      }

      if (children.data.length > 0) {
        if (children.data.length < children.count) {
          const lastChild = children.data[children.data.length - 1];
          if (lastChild.data) {
            showMore.value[lastChild.data?._id] = {
              expandedCount: expandedCount + 1,
              offset: (expandedCount + 1) * limit,
              limit,
              ...(parentId ? { parentId } : {}),
              parentPath,
            };
          }
        }

        let newChildren;

        if (parent) {
          newChildren = [...parent.children, ...children.data];
        } else {
          newChildren = [...children.data];
        }

        if (searchValue.value) {
          nodes.value?.push(...newChildren);
        } else {
          slVueTree.value.updateNode({
            path: parentPath,
            patch: {
              children: [...newChildren],
            },
          });
        }

        showMore.value[node.data?._id as string].loading = false;
        delete showMore.value[node.data?._id as string];
      } else {
        showMore.value[node.data?._id as string].loading = false;
        delete showMore.value[node.data?._id as string];
      }
    }
  };

  const toggleFilterPopover = (event: Event) => {
    searchFilter.value.toggle(event);
  };

  const selectState = async () => {
    loadingSpinner.value = true;
    activeFilter.value = true;

    nodes.value = (
      await props.load(
        null,
        searchValue.value || '',
        0,
        200,
        selectedSearchOption.value,
        selectedFilters.value || null,
      )
    ).data;
    loadingSpinner.value = false;
  };

  const resetFilter = async () => {
    loadingSpinner.value = true;
    if (props.filterOption) {
      selectedFilters.value = [KksState.SAP_PM];
    }
    selectedSearchOption.value = SlVueTreeSearchOptions.STARTS_WITH;
    searchValue.value = '';
    nodes.value = (
      await props.load(
        null,
        searchValue.value || '',
        0,
        200,
        SlVueTreeSearchOptions.STARTS_WITH,
        null,
      )
    ).data;
    activeFilter.value = false;
    loadingSpinner.value = false;
  };

  const selectSearchOption = async () => {
    loadingSpinner.value = true;
    activeFilter.value = true;

    nodes.value = (
      await props.load(
        null,
        searchValue.value || '',
        0,
        200,
        selectedSearchOption.value,
        selectedFilters.value || null,
      )
    ).data;
    loadingSpinner.value = false;
  };

  const isKksPresent = (kks: ITreeSelectKKS) => {
    return !!find(selectedValue.value, (n) => n._id === kks._id);
  };

  /** LYFECYCLE */
  onMounted(() => {
    Object.values(SlVueTreeSearchOptions).forEach((val) => {
      searchOption.value.push(val);
    });

    if (props.filterOption) {
      selectedFilters.value = [KksState.SAP_PM];
    }

    setPreselected();

    document.addEventListener('click', onClickOutside);
  });

  function setPreselected() {
    if (model.value) {
      const uniqueSelectedValues = getUniqueSelectedValues(model.value);

      selectedValue.value = [...uniqueSelectedValues];
      uniqueSelectedValues.forEach((k) => {
        checkBoxes.value[k._id] = true;
      });
    }
  }

  onBeforeUnmount(() => {
    document.removeEventListener('click', onClickOutside);
  });
</script>

<template>
  <!--  HACK: The PrimeVue MultiSelect and Select components are used to force -->
  <!-- loading of needed PrimeVue styles. -->
  <MultiSelect :style="{ display: 'none' }"></MultiSelect>
  <Select :style="{ display: 'none' }"></Select>
  <div class="w-full relative">
    <div
      class="p-multiselect p-component p-inputwrapper p-multiselect-display-chip p-inputwrapper-filled w-full"
      :class="{
        busy,
        'p-multiselect-open': expanded,
        'p-disabled': disabled,
        'p-invalid': isUnathorizedForKksError,
      }"
      @click.stop="expanded = !expanded"
    >
      <div
        class="p-multiselect-label-container"
        data-pc-section="labelcontainer"
      >
        <div class="flex items-center flex-wrap gap-2 p-1">
          <Tag
            v-for="value in selectedValue"
            :key="value._id"
            :style="value.state ? KKSStatesColorTags[value.state] : ''"
            >{{ value[tagKey]
            }}<font-awesome-icon
              v-if="canRemoveLast || selectedValue.length > 1"
              class="cursor-pointer"
              :icon="faX"
              @click.stop="removeElement(value)"
            />
          </Tag>
          <PVInputText
            v-if="!disabled"
            v-model="freeTextInputValue"
            :placeholder="`${t('kksPicker.addValue')}...`"
            :pt="{
              root: {
                style: {
                  border: 'none',
                  'flex-grow': 1,
                  'flex-basis': '9rem',
                  'min-width': '9rem',
                  padding: 0,
                  'box-shadow': 'none',
                  outline: 'none',
                },
              },
            }"
            class="free-text-input"
            :class="{
              busy: isLoadingKksAccess,
            }"
            @input="isUnathorizedForKksError = false"
            @click.stop
            @keydown.enter.prevent="addFromFreeTextInput"
          />
        </div>
      </div>
      <div class="p-multiselect-dropdown" data-pc-section="dropdown">
        <component :is="ChevronDownIcon" class="absolute" />
      </div>
    </div>
    <div v-if="expanded" ref="dropdown" class="custom-dropdown">
      <div>
        <div class="flex pt-3 pl-3 pb-2 pr-2 gap-2">
          <InputGroup name="tree-select-input-group">
            <IconField name="tree-select-icon-field">
              <InputText
                v-model="searchValue"
                name="tree-select-search-field"
                class="w-full"
                :placeholder="t('generic.search')"
                size="small"
                @input="delay(onSearch)"
              />
              <InputIcon
                v-if="searchLoad"
                name="tree-select-search-spinner"
                class="pi pi-spin pi-spinner"
              />
            </IconField>
          </InputGroup>
          <Button
            v-if="showFilterButton"
            severity="secondary"
            variant="text"
            rounded
            @click="toggleFilterPopover"
          >
            <font-awesome-icon v-if="!activeFilter" :icon="faFilter" />
            <font-awesome-icon
              v-else
              style="color: var(--p-primary-color)"
              :icon="faFilterSolid"
            />
          </Button>
          <Popover
            ref="searchFilter"
            name="tree-select-filter-popover"
            class="min-w-60 max-w-96"
            @click.stop
          >
            <div class="p-2 flex flex-col gap-2 w-full">
              <div>
                <span class="font-medium block">
                  {{ t('generic.filter') }}
                </span>
              </div>
              <Select
                v-model="selectedSearchOption"
                name="treeselect-filter-select-search-option"
                :options="searchOption"
                @change="selectSearchOption"
              >
                <template #value>
                  {{ t(`filters.${selectedSearchOption}`) }}
                </template>
                <template #option="slotProps">
                  <div>{{ t(`filters.${slotProps.option}`) }}</div>
                </template>
              </Select>
              <MultiSelect
                v-if="props.filterOption"
                ref="multiSelect"
                v-model="selectedFilters"
                name="treeselect-filter-multi-select-state"
                :options="props.filterOption"
                placeholder="status"
                :show-toggle-all="false"
                @change="selectState"
              >
                <template #value="slotProps">
                  <div
                    v-if="selectedFilters && selectedFilters.length > 0"
                    class="flex flex-wrap gap-2"
                  >
                    <Tag
                      v-for="filter in selectedFilters"
                      :key="filter"
                      :style="selectedFilters ? KKSStatesColorTags[filter] : ''"
                    >
                      {{ filter }}
                    </Tag>
                  </div>
                  <span v-else>
                    {{ t(`kksPicker.${slotProps.placeholder}`) }}
                  </span>
                </template>
                <template #option="slotProps">
                  <div>
                    <Tag
                      :style="
                        slotProps.option
                          ? KKSStatesColorTags[slotProps.option]
                          : ''
                      "
                    >
                      {{ slotProps.option }}
                    </Tag>
                  </div>
                </template>
              </MultiSelect>
              <Button @click.stop="resetFilter">
                <font-awesome-icon :icon="faRotateRight" />
                <span>{{ t('generic.resetAllFilters') }}</span>
              </Button>
            </div>
          </Popover>
        </div>
        <SlVueTreeNext
          ref="slVueTree"
          v-model="nodes"
          class="pr-3 pl-3 pb-3 text-xs"
        >
          <template #toggle="{ node }">
            <div
              class="w-3"
              :style="
                showMore[node.data._id] ? { marginBottom: '1.25rem' } : {}
              "
              @click="loadChildren(node)"
            >
              <div v-if="node.data.childStates || !props.filterOption">
                <font-awesome-icon
                  v-if="spinners[node.data._id]"
                  :icon="faSpinnerThird"
                  spin
                />
                <font-awesome-icon
                  v-else
                  :icon="node.isExpanded ? faChevronDown : faChevronRight"
                />
              </div>
            </div>
          </template>
          <template #title="{ node }">
            <div :style="showMore[node.data._id] ? { height: '3rem' } : {}">
              <div class="flex items-center">
                <span class="ml-1 mr-1">
                  <Checkbox
                    :id="`tree-select-checkbox-${node.data._id}`"
                    v-model="checkBoxes[node.data._id]"
                    type="checkbox"
                    :name="`tree-select-checkbox-${node.data._id}`"
                    binary
                    size="small"
                    @change="chooseElement(node)"
                  />
                </span>
                <slot name="option" :node="node">
                  {{ node.title }}
                </slot>
              </div>
              <div
                v-if="showMore[node.data._id]"
                class="ml-3 absolute cursor-pointer"
                @click="loadMore(node)"
              >
                <span
                  class="font-medium block"
                  style="color: var(--p-primary-color)"
                  ><font-awesome-icon
                    :icon="faRotate"
                    :spin="showMore[node.data._id].loading"
                  />
                  {{ t('generic.loadMore') }}</span
                >
              </div>
            </div>
          </template>
        </SlVueTreeNext>
        <div
          v-if="isDropdownLoading && props.filterOption"
          class="absolute inset-0 bg-opacity-60 flex justify-center items-center z-50"
        >
          <PVProgressSpinner data-testid="tree-select-spinner" />
        </div>
        <div
          v-if="(!nodes?.length || nodes?.length < 1) && !isDropdownLoading"
          class="p-5 w-full flex justify-center items-center"
        >
          <span>{{ t('generic.noDataFound') }}</span>
        </div>
      </div>
    </div>
    <message-wrapper v-if="isUnathorizedForKksError" simple-error class="mt-1">
      {{ t('kksPicker.errors.unauthorizedKks') }}
    </message-wrapper>
  </div>
</template>

<style lang="scss">
  @import 'sl-vue-tree-next/sl-vue-tree-next-minimal.css';
  .sl-vue-tree-next-title {
    display: flex;
    align-items: center;
    margin-top: 3px;
    margin-bottom: 3px;
  }

  .sl-vue-tree-next-selected > .sl-vue-tree-next-node-item {
    background: var(--p-select-option-selected-focus-background);
    color: var(--p-select-option-selected-focus-color);
    border-radius: 4px;
  }

  .custom-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 1000;
    min-width: 100%;
    width: max-content;
    max-height: 28rem;
    background: var(--p-select-overlay-background);
    color: var(--p-select-overlay-color);
    border: 1px solid var(--p-select-overlay-border-color);
    box-shadow: var(--p-select-overlay-shadow);
    border-radius: var(--p-select-overlay-border-radius);
    overflow-y: auto;
    overflow-x: hidden;
  }
  .p-multiselect.p-component.p-inputwrapper {
    &.busy {
      @include pulsating-animation;
    }
  }

  .free-text-input.busy {
    @include pulsating-animation;
  }
</style>
